import React from "react";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BarChartIcon from "@mui/icons-material/BarChart";
import ImageIcon from "@mui/icons-material/Image";
import SummarizeIcon from "@mui/icons-material/Summarize";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { PortfolioAnalysisIcon } from "assets/material-custom-icons";
import { ViewDefinition } from "store/types";

const VIEW_ICONS: Record<string, React.ReactNode> = {
  chart: <PortfolioAnalysisIcon sx={{ fill: "#58667D", width: "15px", height: "15px" }} />,
  data_table: <TableChartIcon />,
  summary_card: <SummarizeIcon />,
  kpi_card: <BarChartIcon />,
  text_card: <TextFieldsIcon />,
  ai_card: <AutoFixHighIcon />
};

interface SidebarProps {
  availableViews: ViewDefinition[];
  logoUrl: string | null;
  onViewDragStart: (view: ViewDefinition) => (e: React.DragEvent) => void;
  onViewDragEnd: () => void;
  onQuickAddCard: (view: ViewDefinition) => void;
  onOpenLogoDrawer: () => void;
  onOpenBackgroundDrawer: () => void;
  onAutoCleanLayout: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  availableViews,
  logoUrl,
  onViewDragStart,
  onViewDragEnd,
  onQuickAddCard,
  onOpenLogoDrawer,
  onOpenBackgroundDrawer,
  onAutoCleanLayout
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        width: 220,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        padding: "16px 0px 16px 16px",
        boxShadow: "0 1px 2px 0 #1212171a"
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Report Views
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Click or drag to add available components to the report
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Stack flexDirection="column" gap="5px" sx={{ p: "0px 16px 0px 0px" }}>
          {availableViews.map((view) => (
            <Paper
              key={view.id}
              draggable
              onDragStart={onViewDragStart(view)}
              onDragEnd={onViewDragEnd}
              onClick={() => onQuickAddCard(view)}
              elevation={0}
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "grab",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#0B4549",
                  bgcolor: "#f8fafc"
                },
                "&:active": {
                  cursor: "grabbing"
                }
              }}
            >
              <Box sx={{ color: "#64748b" }}>{VIEW_ICONS[view.type] || <BarChartIcon />}</Box>
              <Stack direction="column" gap="2px">
                <Typography variant="body2" fontWeight="medium" lineHeight="16px">
                  {view.title}
                </Typography>
                {view.description && (
                  <Typography variant="caption" color="textSecondary" lineHeight="16px">
                    {view.description}
                  </Typography>
                )}
              </Stack>
            </Paper>
          ))}
          <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />
          {/* Tools section */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Tools
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Report building tools
            </Typography>
          </Box>
          {/* Add Logo option */}
          <Paper
            elevation={0}
            onClick={onOpenLogoDrawer}
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.2s",
              bgcolor: "transparent",
              "&:hover": {
                borderColor: "#0B4549",
                bgcolor: "#f8fafc"
              }
            }}
          >
            <Box sx={{ color: logoUrl ? "#0B4549" : "#64748b" }}>
              <AddPhotoAlternateIcon />
            </Box>
            <Stack direction="column" gap="2px">
              <Typography variant="body2" fontWeight="medium" lineHeight="16px">
                {logoUrl ? "Change Logo" : "Add Logo"}
              </Typography>
              <Typography variant="caption" color="textSecondary" lineHeight="16px">
                Add logo before report title
              </Typography>
            </Stack>
          </Paper>
          {/* Add Background option */}
          <Paper
            elevation={0}
            onClick={onOpenBackgroundDrawer}
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "#0B4549",
                bgcolor: "#f8fafc"
              }
            }}
          >
            <Box sx={{ color: "#64748b" }}>
              <ImageIcon />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                Add Background
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Set page background image
              </Typography>
            </Box>
          </Paper>
          {/* Auto-clean layout option */}
          <Paper
            elevation={0}
            onClick={onAutoCleanLayout}
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "#0B4549",
                bgcolor: "#f8fafc"
              }
            }}
          >
            <Box sx={{ color: "#64748b" }}>
              <AutoFixHighIcon />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                Auto-clean Layout
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Reorganize cards to remove gaps
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Box>
    </Paper>
  );
};
